/**
 * RadiusServerForm — Crear / editar servidor RADIUS.
 * Sigue el patrón dv-* de DetailView.css + instructionsUI.md
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  ServerStackIcon,
  WifiIcon,
  LockClosedIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import FormCardsShell from './FormCardsShell';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const EMPTY = {
  name:        '',
  host:        '',
  auth_port:   1812,
  acct_port:   1813,
  description: '',
  status:      'active',
  service_id:  '',
};

const STATUS_COLORS = {
  active:   { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
  inactive: { bg: '#fee2e2', color: '#991b1b', label: 'Inactivo' },
};

const STATUS_CYCLE = ['active', 'inactive'];

export default function RadiusServerForm({ server, companyId, onClose, onSave, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = !!(server && server.id);
  const navigate  = useNavigate();

  /* Modo de la ficha: 'view' entra en LECTURA (es lo que abre el clic en la fila
     del listado), 'edit'/'create' editan. Si no llega nada, se mantiene el
     comportamiento de siempre (editar). */
  const [editing,  setEditing]  = useState(initialMode ? initialMode !== 'view' : true);

  const [form,     setForm]     = useState({ ...EMPTY });
  const [tab,      setTab]      = useState(isCards ? 'resumen' : 'general');
  const [isDirty,  setIsDirty]  = useState(false);
  const [saving,   setSaving]   = useState(false);

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este servidor RADIUS',
    actionLabel: 'pasas a otro RADIUS o cierras la ficha',
    save: () => handleSave({ keepEditing: editing }),
  });
  const [apiError, setApiError] = useState('');
  // Servicios de la empresa: el RADIUS es un servicio (AAA) de la CMDB y aquí
  // solo se elige cuál. Los de tipo `radius` se listan primero.
  const [services,   setServices]   = useState([]);
  const [creating,   setCreating]   = useState(false);
  // Hosts de la CMDB (servidores y VMs) para poder elegir la IP en vez de
  // teclearla, y ubicación del servicio RADIUS vinculado ("dónde corre").
  const [cmdbHosts,  setCmdbHosts]  = useState([]);
  const [runsOn,     setRunsOn]     = useState(null);   // { loading, hosts: [nombres] }
  const [manualHost, setManualHost] = useState(false);  // desbloquea la conexión

  const loadServices = React.useCallback(async () => {
    if (!companyId) return;
    try {
      const list = await api.getServices(companyId);
      const rows = Array.isArray(list) ? list : [];
      rows.sort((a, b) =>
        (a.service_type === 'radius' ? 0 : 1) - (b.service_type === 'radius' ? 0 : 1));
      setServices(rows);
    } catch {
      setServices([]);
    }
  }, [companyId]);

  useEffect(() => { loadServices(); }, [loadServices]);

  // Hosts de la CMDB: servidores (RackElement) y máquinas virtuales
  useEffect(() => {
    if (!companyId) return;
    let alive = true;
    Promise.all([
      api.getServers(companyId).catch(() => ({ data: [] })),
      api.getVMs(companyId).catch(() => ({ data: [] })),
    ]).then(([srv, vms]) => {
      if (!alive) return;
      const list = [];
      const srvRows = Array.isArray(srv) ? srv : (srv?.data || []);
      srvRows.forEach(x => list.push({ name: x.name, ip: x.ip_address || x.domain_name, kind: 'Servidor' }));
      const vmRows = Array.isArray(vms) ? vms : (vms?.data || []);
      vmRows.forEach(x => list.push({ name: x.name, ip: x.mgmt_ip || x.fqdn, kind: 'VM' }));
      setCmdbHosts(list.filter(x => x.name));
    }).catch(() => {});
    return () => { alive = false; };
  }, [companyId]);

  // "Dónde corre" el servicio RADIUS vinculado (relaciones `runs_on` del diseñador)
  useEffect(() => {
    const svcId = form.service_id;
    if (!svcId || !companyId) { setRunsOn(null); return; }
    let alive = true;
    setRunsOn({ loading: true, hosts: [] });
    api.sdGetCiGraph('service', parseInt(svcId, 10), companyId, { depth: 2, direction: 'out', storage: false })
      .then(p => {
        if (!alive) return;
        const graph = p?.data || p || {};
        const names = [...new Set((graph.nodes || [])
          .filter(n => n.dir === 'out' && n.ci_type !== 'service')
          .map(n => n.name)
          .filter(Boolean))];
        setRunsOn({ loading: false, hosts: names });
      })
      .catch(() => { if (alive) setRunsOn({ loading: false, hosts: [] }); });
    return () => { alive = false; };
  }, [form.service_id, companyId]);

  // Host derivado del servicio: se resuelve el nombre del equipo contra los
  // hosts de la CMDB para quedarnos con su IP.
  const derivedHost = useMemo(() => {
    if (!runsOn?.hosts?.length) return '';
    for (const name of runsOn.hosts) {
      const match = cmdbHosts.find(h => h.name === name && h.ip);
      if (match?.ip) return match.ip;
    }
    return runsOn.hosts[0];
  }, [runsOn, cmdbHosts]);

  const serviceLinked = !!form.service_id;
  const connectionLocked = serviceLinked && !manualHost && !!derivedHost;

  // Con servicio vinculado, el host sale del servicio (dónde corre).
  useEffect(() => {
    if (connectionLocked && derivedHost && form.host !== derivedHost) {
      setForm(f => ({ ...f, host: derivedHost }));
      setIsDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionLocked, derivedHost]);

  // Inicializar formulario
  useEffect(() => {
    if (server && server.id) {
      setForm({
        name:        server.name        || '',
        host:        server.host        || '',
        auth_port:   server.auth_port   ?? 1812,
        acct_port:   server.acct_port   ?? 1813,
        description: server.description || '',
        status:      server.status      || 'active',
        service_id:  server.service_id != null ? String(server.service_id) : '',
      });
    } else {
      setForm({ ...EMPTY });
    }
    setIsDirty(false);
  }, [server]);

  const sf = (k, v) => { setForm(f => ({ ...f, [k]: v })); setIsDirty(true); };
  const handleChange = e => sf(e.target.name, e.target.type === 'number' ? e.target.value : e.target.value);

  const handleDiscard = () => {
    if (server && server.id) {
      setForm({
        name:        server.name        || '',
        host:        server.host        || '',
        auth_port:   server.auth_port   ?? 1812,
        acct_port:   server.acct_port   ?? 1813,
        description: server.description || '',
        status:      server.status      || 'active',
        service_id:  server.service_id != null ? String(server.service_id) : '',
      });
    } else {
      setForm({ ...EMPTY });
    }
    setIsDirty(false);
    setApiError('');
  };

  /** Crea un servicio CMDB de tipo `radius` y lo vincula de una vez. */
  const handleCreateService = async () => {
    setCreating(true);
    setApiError('');
    try {
      const label = form.name.trim() || 'RADIUS';
      const created = await api.createService(
        { name: label, display_name: label, service_type: 'radius' },
        companyId,
      );
      if (created && created.id) {
        await loadServices();
        sf('service_id', String(created.id));
      }
    } catch (e) {
      setApiError(e.message || 'No se pudo crear el servicio RADIUS');
    } finally {
      setCreating(false);
    }
  };

  const openDesigner = () => {
    if (form.service_id) navigate(`/services/designer?service=${form.service_id}`);
  };

  const handleSave = async ({ keepEditing = false } = {}) => {
    if (!form.name.trim()) { setApiError('El nombre es obligatorio.'); return false; }
    if (!form.host.trim()) { setApiError('El host/IP es obligatorio.'); return false; }
    setSaving(true); setApiError('');
    try {
      const payload = {
        ...form,
        auth_port: parseInt(form.auth_port, 10) || 1812,
        acct_port: parseInt(form.acct_port, 10) || 1813,
        service_id: form.service_id ? parseInt(form.service_id, 10) : null,
      };
      let creado = null;
      if (isEditing) await api.updateWifiRadiusServer(server.id, payload, companyId);
      else           creado = registroGuardado(await api.createWifiRadiusServer(payload, companyId));
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las zonas que necesitan estar guardado.
      onSave?.(creado);
      setIsDirty(false);
      if (isCards && !keepEditing) setEditing(false);
      return true;
    } catch (e) {
      setApiError(e.message || 'Error al guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  /* El borrado vive en el listado (con el diálogo único del CMDB): aquí no hay
     botón de borrar ni `window.confirm`. */

  const statusInfo  = STATUS_COLORS[form.status] || STATUS_COLORS.active;
  const ssidCount   = server?.ssid_count ?? 0;

  // ── Datos de la variante tarjetas ────────────────────────────────────────
  const ESTADO_COLOR = { active: '#16a34a', inactive: '#dc2626' };
  const estadoColor = ESTADO_COLOR[form.status] || '#94a3b8';
  const paneClass = isCards ? 'dv-tab-pane sf2-pane' : 'dv-tab-pane';
  const servicioVinculado = services.find(s => String(s.id) === String(form.service_id));
  const servicioLabel = servicioVinculado ? (servicioVinculado.display_name || servicioVinculado.name) : null;
  /** Cancelar edición: recarga lo guardado y sale de edición **sin cerrar** la ficha. */
  const handleCancelEdit = () => { handleDiscard(); setEditing(false); };

  // En tarjetas no hay pestaña «General»: servidor, conexión y servicio van al Resumen.
  const tabsDef = isCards
    ? [
        { key: 'resumen', label: 'General' },
        { key: 'notas',   label: 'Notas' },
      ]
    : [
        { key: 'general', label: 'General' },
        { key: 'notas',   label: 'Notas' },
      ];

  return (
    <div className="dv-overlay">

      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon style={{ width: 15, height: 15 }} />
          Servidores RADIUS
        </button>
        <DetailCrumb label={form.name} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Servidor RADIUS"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {apiError && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 300 }}>
            ⚠ {apiError}
          </span>
        )}
        <div className="dv-topbar-actions">
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
            </button>
          ) : (
          <>
          <span
            className="dv-status-badge"
            style={{ background: statusInfo.bg, color: statusInfo.color, cursor: editing ? 'pointer' : 'default' }}
            title={editing ? 'Clic para cambiar estado' : statusInfo.label}
            onClick={() => {
              if (!editing) return;
              const idx = STATUS_CYCLE.indexOf(form.status);
              sf('status', STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length]);
            }}
          >
            ● {statusInfo.label}
          </span>
          {!editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                <CheckIcon style={{ width: 14, height: 14 }} /> Editar
              </button>
            </>
          ) : (
            <>
              {isDirty && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
                  <XMarkIcon style={{ width: 14, height: 14 }} /> Descartar
                </button>
              )}
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={handleSave}
                disabled={saving || !isDirty}
              >
                {saving
                  ? <span className="spinner-border spinner-border-sm" />
                  : <CheckIcon style={{ width: 14, height: 14 }} />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
          </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <div className="dv-smart-btn" title="Host / IP del servidor">
          <span className="dv-smart-btn-icon">
            <ServerStackIcon style={{ width: 18, height: 18, color: '#2563eb' }} />
          </span>
          <span className="dv-smart-btn-value" style={{ fontFamily: 'monospace', fontSize: '0.78rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {form.host || '—'}
          </span>
          <span className="dv-smart-btn-label">Host / IP</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" title="Puerto de autenticación">
          <span className="dv-smart-btn-icon">
            <span style={{ fontSize: 16 }}>🔑</span>
          </span>
          <span className="dv-smart-btn-value">
            {form.auth_port || 1812}
          </span>
          <span className="dv-smart-btn-label">Auth (UDP)</span>
        </div>

        <div className="dv-smart-sep" />

        <div className="dv-smart-btn" title="Puerto de accounting">
          <span className="dv-smart-btn-icon">
            <span style={{ fontSize: 16 }}>📊</span>
          </span>
          <span className="dv-smart-btn-value">
            {form.acct_port || 1813}
          </span>
          <span className="dv-smart-btn-label">Acct (UDP)</span>
        </div>

        {isEditing && (
          <>
            <div className="dv-smart-sep" />
            <div className="dv-smart-btn" title="SSIDs que usan este servidor">
              <span className="dv-smart-btn-icon">
                <WifiIcon style={{ width: 18, height: 18, color: ssidCount > 0 ? '#6d28d9' : '#94a3b8' }} />
              </span>
              <span className="dv-smart-btn-value" style={{ color: ssidCount > 0 ? '#6d28d9' : '#94a3b8' }}>
                {ssidCount}
              </span>
              <span className="dv-smart-btn-label">SSIDs</span>
            </div>
          </>
        )}

        {isEditing && (
          <DocSmartButton elementType="radius_server" elementId={server.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={e => e.preventDefault()}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <>
                <div className="sf2-head">
                  <div className="sf2-head-icon" aria-hidden="true">
                    <LockClosedIcon style={{ width: 20, height: 20 }} />
                  </div>
                  <div className="sf2-head-title">
                    <div className="sf2-title-row">
                      <input
                        className="sf2-head-input"
                        value={form.name}
                        onChange={e => sf('name', e.target.value)}
                        placeholder="Nombre del servidor RADIUS…"
                        disabled={!editing}
                      />
                      {editing ? (
                        <>
                          <button
                            type="button"
                            className="sf2-title-btn sf2-title-btn--ok"
                            onClick={handleSave}
                            disabled={saving}
                            title="Guardar cambios"
                          >
                            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                          </button>
                          <button
                            type="button"
                            className="sf2-title-btn"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            title="Cancelar la edición y descartar cambios"
                          >
                            <XMarkIcon />
                          </button>
                        </>
                      ) : (
                        <button type="button" className="sf2-title-btn" onClick={() => setEditing(true)} title="Editar la ficha">
                          <PencilSquareIcon />
                        </button>
                      )}
                    </div>
                    <div className="sf2-crumb">
                      {[form.host, servicioLabel].filter(Boolean).join(' › ')}
                    </div>
                  </div>
                  <div className="sf2-badges">
                    <span className="sf2-badge"><i style={{ '--c': estadoColor }} />{statusInfo.label}</span>
                    <span className="sf2-badge">RADIUS</span>
                    {servicioLabel && <span className="sf2-badge">{servicioLabel}</span>}
                  </div>
                </div>
                <div className="sf2-chips">
                  <span className="sf2-chip sf2-chip--flat"><b>{form.auth_port || 1812}</b><span>auth UDP</span></span>
                  <span className="sf2-chip sf2-chip--flat"><b>{form.acct_port || 1813}</b><span>acct UDP</span></span>
                  <span className="sf2-chip sf2-chip--flat"><b>{ssidCount}</b><span>SSIDs</span></span>
                  {server?.id && (
                    <DocSmartButton elementType="radius_server" elementId={server.id} elementName={form.name} companyId={companyId} variant="chip" />
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Título */}
                <input
                  className="dv-record-title"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Nombre del servidor RADIUS…"
                  autoFocus
                />
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
            {tabsDef.map(t => (
              <TabButton
                key={t.key}
                type="button"
                className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </TabButton>
            ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset" disabled={!editing}>

          {/* ── Pestaña: Resumen (solo tarjetas) ── */}
          {isCards && tab === 'resumen' && (
            <div className="dv-tab-pane">
              <div className="sf2-hl" style={{ marginBottom: 12 }}>
                <div className="sf2-kpi"><span>Host / IP</span><b style={{ fontSize: '0.95rem' }}>{form.host || '—'}</b></div>
                <div className="sf2-kpi"><span>Auth (UDP)</span><b>{form.auth_port || 1812}</b></div>
                <div className="sf2-kpi"><span>Acct (UDP)</span><b>{form.acct_port || 1813}</b></div>
                <div className="sf2-kpi"><span>SSIDs que lo usan</span><b>{ssidCount}</b></div>
                <div className="sf2-kpi"><span>Estado</span><b>{statusInfo.label}</b></div>
              </div>

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Servidor{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field"><label>Nombre</label><div className="v">{form.name || '—'}</div></div>
                    <div className="sf2-field">
                      <label>Estado</label>
                      {editing ? (
                        <select value={form.status} onChange={e => sf('status', e.target.value)}>
                          <option value="active">Activo</option>
                          <option value="inactive">Inactivo</option>
                        </select>
                      ) : <div className="v">{statusInfo.label}</div>}
                    </div>
                  </div>
                  <span
                    className="sf2-link"
                    role="button"
                    tabIndex={0}
                    onClick={() => setTab('notas')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('notas'); } }}
                  >Ver notas →</span>
                </div>

                <div className="sf2-card">
                  <h3>Conexión{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Host / IP *</label>
                      {editing ? (
                        <>
                          <input
                            value={form.host}
                            onChange={e => sf('host', e.target.value)}
                            placeholder="10.0.1.5 o radius.empresa.com"
                            list="radius-cmdb-hosts-cards"
                            disabled={connectionLocked}
                          />
                          <datalist id="radius-cmdb-hosts-cards">
                            {cmdbHosts.filter(h => h.ip).map(h => (
                              <option key={`${h.kind}-${h.name}`} value={h.ip} label={`${h.name} (${h.kind})`} />
                            ))}
                          </datalist>
                        </>
                      ) : <div className="v">{form.host || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Puerto Auth (UDP)</label>
                      {editing
                        ? <input type="number" min="1" max="65535" value={form.auth_port} onChange={e => sf('auth_port', e.target.value)} placeholder="1812" disabled={connectionLocked} />
                        : <div className="v">{form.auth_port || 1812}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Puerto Acct (UDP)</label>
                      {editing
                        ? <input type="number" min="1" max="65535" value={form.acct_port} onChange={e => sf('acct_port', e.target.value)} placeholder="1813" disabled={connectionLocked} />
                        : <div className="v">{form.acct_port || 1813}</div>}
                    </div>
                    {editing && (
                      <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                        <label>Elegir host de la CMDB</label>
                        <select
                          value=""
                          onChange={e => { if (e.target.value) sf('host', e.target.value); }}
                          disabled={connectionLocked || cmdbHosts.length === 0}
                        >
                          <option value="">
                            {cmdbHosts.length ? '— Selecciona un servidor o VM —' : '— No hay equipos con IP en la CMDB —'}
                          </option>
                          {cmdbHosts.map(h => (
                            <option key={`sel-${h.kind}-${h.name}`} value={h.ip || h.name}>
                              {h.name} ({h.kind}){h.ip ? ` · ${h.ip}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  {serviceLinked && connectionLocked && (
                    <span className="sf2-hint">
                      🔗 Host y puertos heredados del servicio vinculado — <b>{derivedHost}</b>.
                      {editing && (
                        <label className="sf2-switch sf2-switch--sm" style={{ marginTop: 6 }}>
                          <input type="checkbox" checked={manualHost} onChange={e => setManualHost(e.target.checked)} />
                          <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                          <span className="sf2-switch-label">Introducir manualmente</span>
                        </label>
                      )}
                    </span>
                  )}
                  {serviceLinked && !connectionLocked && (
                    <span className="sf2-hint">
                      ⚠️ El servicio vinculado no indica dónde corre (relación «runs_on» en el diseñador).
                    </span>
                  )}
                  <span className="sf2-hint">
                    🔑 El secreto compartido no se almacena en el CMDB: configúralo en el controlador / AP.
                  </span>
                </div>

                <div className="sf2-card" style={{ gridColumn: '1 / -1' }}>
                  <h3>Servicio en la CMDB{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Servicio RADIUS</label>
                      {editing ? (
                        <select value={form.service_id} onChange={e => sf('service_id', e.target.value)}>
                          <option value="">— Sin servicio vinculado —</option>
                          {services.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.display_name || s.name}{s.service_type === 'radius' ? ' · RADIUS' : ''}
                            </option>
                          ))}
                        </select>
                      ) : <div className="v">{servicioLabel || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Dónde corre</label>
                      <div className="v">
                        {runsOn?.loading
                          ? 'Consultando…'
                          : (runsOn?.hosts?.length ? runsOn.hosts.join(', ') : (serviceLinked ? 'Sin definir en el diseñador' : '—'))}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
                    {editing && (
                      <button type="button" className="sf2-link" onClick={handleCreateService} disabled={creating}>
                        {creating ? 'Creando…' : '＋ Crear servicio RADIUS'}
                      </button>
                    )}
                    {form.service_id && (
                      <button type="button" className="sf2-link" onClick={openDesigner}>Ver en el diseñador →</button>
                    )}
                  </div>
                  <span className="sf2-hint">
                    Este servidor es la configuración (host y puertos) de un servicio CMDB: ahí se define dónde corre
                    y a quién autentica (WiFi 802.1X, VPN, switches…).
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ── Pestaña: General ── */}
          {tab === 'general' && (
            <div className={paneClass}>

              {/* Identificación */}
              <div className="dv-section">
                <div className="dv-section-title">Identificación</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <select className="dv-select" name="status" value={form.status} onChange={handleChange}>
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Conexión */}
              <div className="dv-section">
                <div className="dv-section-title">Conexión</div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Host / IP <span className="text-danger">*</span></label>
                    <input
                      className="dv-input"
                      name="host"
                      value={form.host}
                      onChange={handleChange}
                      placeholder="10.0.1.5 o radius.empresa.com"
                      list="radius-cmdb-hosts"
                      disabled={connectionLocked}
                    />
                    <datalist id="radius-cmdb-hosts">
                      {cmdbHosts.filter(h => h.ip).map(h => (
                        <option key={`${h.kind}-${h.name}`} value={h.ip} label={`${h.name} (${h.kind})`} />
                      ))}
                    </datalist>
                    <div className="form-text">Dirección IP o FQDN del servidor RADIUS.</div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Puerto Auth (UDP)</label>
                    <input
                      className="dv-input"
                      name="auth_port"
                      type="number"
                      min="1"
                      max="65535"
                      value={form.auth_port}
                      onChange={handleChange}
                      placeholder="1812"
                      disabled={connectionLocked}
                    />
                    <div className="form-text">Autenticación. Por defecto: 1812</div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Puerto Acct (UDP)</label>
                    <input
                      className="dv-input"
                      name="acct_port"
                      type="number"
                      min="1"
                      max="65535"
                      value={form.acct_port}
                      onChange={handleChange}
                      placeholder="1813"
                      disabled={connectionLocked}
                    />
                    <div className="form-text">Accounting. Por defecto: 1813</div>
                  </div>

                  {/* Elegir el host de la CMDB en vez de teclearlo */}
                  <div className="col-md-6">
                    <label className="form-label">Elegir host de la CMDB</label>
                    <select
                      className="dv-select"
                      value=""
                      onChange={e => { if (e.target.value) sf('host', e.target.value); }}
                      disabled={connectionLocked || cmdbHosts.length === 0}
                    >
                      <option value="">
                        {cmdbHosts.length ? '— Selecciona un servidor o VM —' : '— No hay equipos con IP en la CMDB —'}
                      </option>
                      {cmdbHosts.map(h => (
                        <option key={`sel-${h.kind}-${h.name}`} value={h.ip || h.name}>
                          {h.name} ({h.kind}){h.ip ? ` · ${h.ip}` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">Copia la IP del equipo donde corre el RADIUS.</div>
                  </div>

                  {serviceLinked && (
                    <div className="col-12">
                      {connectionLocked ? (
                        <div style={{ padding: '0.6rem 0.9rem', background: 'rgba(59,130,246,.12)', border: '1px solid rgba(59,130,246,.4)', borderRadius: 6, fontSize: '0.82rem', color: '#93c5fd' }}>
                          🔗 Host y puertos heredados del servicio CMDB vinculado —{' '}
                          <strong>{derivedHost}</strong> (dónde corre el servicio).
                          <label style={{ marginLeft: 12, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                            <input
                              type="checkbox"
                              checked={manualHost}
                              onChange={e => setManualHost(e.target.checked)}
                              style={{ marginRight: 6 }}
                            />
                            Introducir manualmente
                          </label>
                        </div>
                      ) : (
                        <div style={{ padding: '0.6rem 0.9rem', background: 'rgba(245,158,11,.15)', border: '1px solid rgba(245,158,11,.4)', borderRadius: 6, fontSize: '0.82rem', color: '#fcd34d' }}>
                          ⚠️ El servicio vinculado no indica dónde corre (relación «runs_on» en el diseñador),
                          así que el host se escribe a mano. Configúralo en el diseñador para heredarlo automáticamente.
                        </div>
                      )}
                    </div>
                  )}

                  <div className="col-12">
                    <div style={{ padding: '0.6rem 0.9rem', background: 'rgba(245,158,11,.15)', border: '1px solid rgba(245,158,11,.4)', borderRadius: 6, fontSize: '0.82rem', color: '#fcd34d' }}>
                      🔑 El secreto compartido (shared secret) <strong>no se almacena</strong> en el CMDB. Configúralo directamente en el controlador / AP.
                    </div>
                  </div>
                </div>
              </div>

              {/* Servicio en la CMDB: RADIUS es un servicio (AAA), no solo WiFi */}
              <div className="dv-section">
                <div className="dv-section-title">Servicio en la CMDB</div>
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="form-label">Servicio RADIUS</label>
                    <select
                      className="dv-select"
                      name="service_id"
                      value={form.service_id}
                      onChange={handleChange}
                    >
                      <option value="">— Sin servicio vinculado —</option>
                      {services.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.display_name || s.name}
                          {s.service_type === 'radius' ? ' · RADIUS' : ''}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Este servidor es la configuración (host y puertos) de un servicio CMDB:
                      ahí se define dónde corre y a quién autentica (WiFi 802.1X, VPN, switches…).
                    </div>
                    {serviceLinked && runsOn && (
                      <div className="form-text" style={{ marginTop: 6 }}>
                        {runsOn.loading ? (
                          '📍 Consultando dónde corre el servicio…'
                        ) : runsOn.hosts.length ? (
                          <>📍 Dónde corre: <strong>{runsOn.hosts.join(', ')}</strong>{derivedHost ? ` → host ${derivedHost}` : ''}</>
                        ) : (
                          '📍 El servicio no tiene definido dónde corre en el diseñador.'
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-md-4" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="dv-btn dv-btn-secondary"
                      onClick={handleCreateService}
                      disabled={creating}
                      title="Crea un servicio CMDB de tipo RADIUS y lo vincula"
                    >
                      {creating ? 'Creando…' : '＋ Crear servicio RADIUS'}
                    </button>
                    {form.service_id ? (
                      <button
                        type="button"
                        className="dv-btn dv-btn-secondary"
                        onClick={openDesigner}
                        title="Ver este servicio en el diseñador (dónde corre, a quién autentica)"
                      >
                        Ver en el diseñador ↗
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Pestaña: Notas ── */}
          {tab === 'notas' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Descripción / Notas</div>
                <div className="row g-3">
                  <div className="col-12">
                    <textarea
                      className="dv-textarea"
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      rows={6}
                      placeholder="Descripción, responsable, ubicación del servidor, notas de configuración…"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          </fieldset>
          <FormDirtyDock
            show={isCards && isDirty}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={handleSave}
          />
          </FormCardsShell>

        </form>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="radius_server"
          ciId={server?.id}
          tipoLegible="el servidor RADIUS"
        />
      </div>
    </div>
  );
}
